(function () {
  'use strict';

  // The section renders one script tag per instance — run the script only once.
  if (window.__crReviewsInit) return;
  window.__crReviewsInit = true;

  const MAX_DOTS = 5; // most pagination dots shown at once (sliding window)
  const MODAL_ANIM_MS = 250; // keep in sync with .cr-modal transitions in the CSS

  // One request per API URL, shared by every section instance on the page.
  const reviewsRequests = new Map();
  function fetchJudgemeReviews(url) {
    if (!reviewsRequests.has(url)) {
      const request = fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(`Reviews API responded ${res.status}`);
          return res.json();
        })
        .then(data => (Array.isArray(data.reviews) ? data.reviews : []))
        .catch(e => {
          console.error('[CustomReviews] Reviews API fetch failed', e);
          reviewsRequests.delete(url);
          return [];
        });
      reviewsRequests.set(url, request);
    }
    return reviewsRequests.get(url);
  }

  document.querySelectorAll('.cr-section').forEach(initSection);

  // Show a loading state on the modal "Add to cart" button while the theme's cart
  // pipeline runs. Capture phase is required: the theme's .custom-add-cart handler
  // calls stopPropagation(), but same-element (document) listeners still fire.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cr-modal .custom-add-cart');
    if (!btn || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');
    btn.disabled = true;
    const reset = () => {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    };
    // Cleared when the add completes (cart pipeline dispatches this event); the
    // timeout is a fallback in case the add fails and no event fires.
    document.addEventListener('eurus:cart:items-changed', reset, { once: true });
    setTimeout(reset, 6000);
  }, true);

  // Transparent stand-in while card images wait for the first interaction (assets/interaction-media.js)
  const PLACEHOLDER_SRC = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  function initSection(section) {
    const deferMedia = section.hasAttribute('data-defer-media');
    const imgSrc = (url) => (deferMedia && document.documentElement.dataset.mediaReleased !== 'true'
      ? `src="${PLACEHOLDER_SRC}" data-src="${url}" data-interaction-media`
      : `src="${url}"`);

    const config = {
      reviewsApi: section.dataset.reviewsApi,
      productId: section.dataset.productId,
      collectionHandle: section.dataset.collectionHandle,
      pageContext: section.dataset.context,
      showJudgeme: section.dataset.showJudgeme === 'true',
      showManual: section.dataset.showManual === 'true',
      showProductReviews: section.dataset.showProductReviews !== 'false',
      showSimilarReviews: section.dataset.showSimilarReviews !== 'false',
      filters: {
        all: section.dataset.filterAll !== 'false',
        photos: section.dataset.filterPhotos !== 'false',
        fivestar: section.dataset.filterFiveStar !== 'false',
        curated: section.dataset.filterCurated !== 'false',
        concern: section.dataset.filterConcern !== 'false',
        skinType: section.dataset.filterSkintype !== 'false',
        products: section.dataset.filterProducts !== 'false',
      },
    };

    const els = {
      carousel: section.querySelector('[data-cr-carousel]'),
      filters: section.querySelector('[data-cr-filters]'),
      dots: section.querySelector('[data-cr-dots]'),
      prevBtn: section.querySelector('[data-cr-prev]'),
      nextBtn: section.querySelector('[data-cr-next]'),
      totalReviews: section.querySelector('[data-cr-total-reviews]'),
      avgRating: section.querySelector('[data-cr-avg-rating]'),
      manualScript: section.querySelector('[data-cr-manual-reviews]'),
      productsScript: section.querySelector('[data-cr-products]'),
      similarProductsScript: section.querySelector('[data-cr-similar-products]'),
    };

    const state = {
      allReviews: [],
      filteredReviews: [],
      activeFilter: 'all',
      selectedConcerns: new Set(),
      selectedSkinTypes: new Set(),
      productMap: {},
      productMapById: {},
      similarProductIds: new Set(),
      similarProductHandles: new Set(),
    };

    loadReviews();

    async function loadReviews() {
      let manualReviews = [];
      let judgemeReviews = [];

      if (config.showManual && els.manualScript) {
        try {
          manualReviews = JSON.parse(els.manualScript.textContent.trim());
        } catch (e) {
          console.error('[CustomReviews] Manual reviews parse failed', e);
        }
      }

      let products = [];
      if (els.productsScript) {
        try {
          products = JSON.parse(els.productsScript.textContent.trim());
          products.forEach(p => {
            state.productMap[p.handle] = p;
            if (p.id != null) state.productMapById[p.id] = p;
          });
        } catch (e) {
          console.error('[CustomReviews] Products parse failed', e);
        }
      }

      // Similar products (from the product's custom.similar_judgeme_products_reviews
      // metafield) whose reviews should also appear on this product page.
      if (els.similarProductsScript) {
        try {
          const similar = JSON.parse(els.similarProductsScript.textContent.trim());
          similar.forEach(p => {
            if (p.id != null) {
              state.similarProductIds.add(String(p.id));
              if (!state.productMapById[p.id]) state.productMapById[p.id] = p;
            }
            if (p.handle) {
              state.similarProductHandles.add(p.handle);
              if (!state.productMap[p.handle]) state.productMap[p.handle] = p;
            }
          });
        } catch (e) {
          console.error('[CustomReviews] Similar products parse failed', e);
        }
      }

      if (config.showJudgeme && config.reviewsApi) {
        judgemeReviews = await fetchJudgemeReviews(config.reviewsApi);
      }

      manualReviews = filterByContext(manualReviews);
      judgemeReviews = filterByContext(judgemeReviews);

      state.allReviews = mergeAndSort([...manualReviews, ...judgemeReviews]);
      state.filteredReviews = state.allReviews;

      renderStats();
      renderFilters();
      renderCarousel();
      setupCarouselNav();
      setupResizeHandler();

      console.log(`[CustomReviews] Loaded ${state.allReviews.length} reviews (${judgemeReviews.length} Judge.me + ${manualReviews.length} manual)`);
    }

    // ============================================
    // Merge & sort
    // ============================================
    function mergeAndSort(reviews) {
      const manual = reviews.filter(r => r.source === 'manual');
      const judgeme = reviews.filter(r => r.source === 'judgeme');

      manual.sort((a, b) => {
        const orderA = a.display_order ?? 9999;
        const orderB = b.display_order ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });

      judgeme.sort((a, b) => {
        if (a.rating !== b.rating) return b.rating - a.rating;
        const aHasPics = (a.has_pictures || (a.pictures && a.pictures.length > 0)) ? 1 : 0;
        const bHasPics = (b.has_pictures || (b.pictures && b.pictures.length > 0)) ? 1 : 0;
        if (aHasPics !== bHasPics) return bHasPics - aHasPics;
        const aFeatured = (a.featured || a.is_curated) ? 1 : 0;
        const bFeatured = (b.featured || b.is_curated) ? 1 : 0;
        if (aFeatured !== bFeatured) return bFeatured - aFeatured;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });

      // No truncation: the set is already curated (featured Judge.me + manual).
      return [...manual, ...judgeme];
    }

    // ============================================
    // Filter by page context
    // ============================================
    function filterByContext(reviews) {
      if (config.pageContext === 'product' && config.productId) {
        const currentHandle = window.location.pathname
          .replace(/^\/products\//, '').split('?')[0].split('/')[0];

        const allowedIds = new Set();
        const allowedHandles = new Set();

        // This product's own reviews (toggle).
        if (config.showProductReviews) {
          allowedIds.add(String(config.productId));
          if (currentHandle) allowedHandles.add(currentHandle);
        }
        // Reviews of similar products from the metafield (toggle).
        if (config.showSimilarReviews) {
          state.similarProductIds.forEach(id => allowedIds.add(String(id)));
          state.similarProductHandles.forEach(h => allowedHandles.add(h));
        }

        if (!allowedIds.size && !allowedHandles.size) return [];
        return reviews.filter(r => {
          if (r.product_id != null && allowedIds.has(String(r.product_id))) return true;
          if (r.product_handle && allowedHandles.has(r.product_handle)) return true;
          return false;
        });
      }

      if (config.pageContext === 'collection') {
        const allowedHandles = Object.keys(state.productMap);
        const allowedIds = Object.values(state.productMap).map(p => p.id);
        return reviews.filter(r => {
          if (r.product_handle && allowedHandles.includes(r.product_handle)) return true;
          if (r.product_id && allowedIds.includes(r.product_id)) return true;
          return false;
        });
      }

      return reviews;
    }

    // ============================================
    // Stats Bar
    // ============================================
    function renderStats() {
      if (!els.totalReviews && !els.avgRating) return;
      const reviews = state.filteredReviews;
      const total = reviews.length;
      const avg = total
        ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / total).toFixed(1)
        : '0.0';

      if (els.totalReviews) {
        els.totalReviews.textContent = total >= 1000
          ? `${(total / 1000).toFixed(1)}k+`
          : `${total}+`;
      }
      if (els.avgRating) els.avgRating.textContent = avg;
    }

    // ============================================
    // Filter Chips
    // ============================================
    function renderFilters() {
      if (!els.filters) return;

      const typeFilters = [];
      if (config.filters.all) typeFilters.push({ id: 'all', label: 'All' });
      if (config.filters.photos) typeFilters.push({ id: 'photos', label: 'Photo reviews' });
      if (config.filters.fivestar) typeFilters.push({ id: '5star', label: '5★' });
      if (config.filters.curated) typeFilters.push({ id: 'curated', label: 'Curated' });

      const concernValues = config.filters.concern ? gatherValueCounts('concern') : [];
      const skinTypeValues = config.filters.skinType ? gatherValueCounts('skin_type') : [];

      let productFilters = [];
      if (config.filters.products && config.pageContext !== 'product') {
        const productCounts = new Map();
        state.allReviews.forEach(r => {
          if (r.product_handle && r.product_title) {
            const key = r.product_handle;
            if (!productCounts.has(key)) {
              productCounts.set(key, { handle: r.product_handle, title: r.product_title, count: 0 });
            }
            productCounts.get(key).count++;
          }
        });
        productFilters = Array.from(productCounts.values())
          .sort((a, b) => b.count - a.count)
          .map(p => ({
            id: `product:${p.handle}`,
            label: p.title,
            count: p.count,
          }));
      }

      const typeChipsHtml = typeFilters.map(f => `
        <button class="cr-filter ${f.id === state.activeFilter ? 'is-active' : ''}"
                data-filter="${f.id}">
          ${escapeHtml(f.label)}
        </button>
      `).join('');

      const concernDropdownHtml = concernValues.length
        ? renderDropdown('concern', 'Concern', concernValues) : '';
      const skinTypeDropdownHtml = skinTypeValues.length
        ? renderDropdown('skin_type', 'Skin Type', skinTypeValues) : '';

      const productChipsHtml = productFilters.length ? `
        <div class="cr-filters-row cr-filters-products">
          ${productFilters.map(f => `
            <button class="cr-filter cr-filter-product ${f.id === state.activeFilter ? 'is-active' : ''}"
                    data-filter="${f.id}">
              ${escapeHtml(f.label)}
            </button>
          `).join('')}
        </div>
      ` : '';

      els.filters.innerHTML = `
        <div class="cr-filters-row cr-filters-types">
          ${typeChipsHtml}
          ${concernDropdownHtml}
          ${skinTypeDropdownHtml}
        </div>
        ${productChipsHtml}
      `;

      els.filters.querySelectorAll('.cr-filter[data-filter]').forEach(btn => {
        btn.addEventListener('click', () => applyTypeFilter(btn.dataset.filter));
      });

      setupDropdowns();
    }

    // Collect distinct values + counts for a list-type field (concern / skin_type)
    function gatherValueCounts(field) {
      const counts = new Map();
      state.allReviews.forEach(r => {
        const vals = Array.isArray(r[field]) ? r[field] : (r[field] ? [r[field]] : []);
        vals.forEach(v => {
          if (!v) return;
          counts.set(v, (counts.get(v) || 0) + 1);
        });
      });
      return Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count);
    }

    function renderDropdown(key, label, values) {
      const items = values.map(v => `
        <li>
          <label class="cr-check">
            <input type="checkbox" value="${escapeHtml(v.value)}" data-cr-check="${key}">
            <span class="cr-check-label">${escapeHtml(v.value)}</span>
          </label>
        </li>
      `).join('');

      return `
        <div class="cr-dropdown" data-cr-dropdown="${key}">
          <button type="button" class="cr-filter cr-dropdown-toggle" data-cr-dropdown-toggle aria-expanded="false">
            <span class="cr-dropdown-label">${escapeHtml(label)}</span>
            <span class="cr-dropdown-caret" aria-hidden="true">▾</span>
          </button>
          <div class="cr-dropdown-panel" hidden>
            <div class="cr-dropdown-head">
              <span class="cr-dropdown-count" data-cr-dropdown-count>0 selected</span>
              <button type="button" class="cr-dropdown-reset" data-cr-dropdown-reset>Reset</button>
            </div>
            <ul class="cr-dropdown-list">${items}</ul>
          </div>
        </div>
      `;
    }

    function setupDropdowns() {
      els.filters.querySelectorAll('.cr-dropdown').forEach(dd => {
        const key = dd.dataset.crDropdown;
        const toggle = dd.querySelector('[data-cr-dropdown-toggle]');
        const panel = dd.querySelector('.cr-dropdown-panel');

        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = !panel.hidden;
          closeAllDropdowns();
          if (!isOpen) {
            panel.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
            dd.classList.add('is-open');
          }
        });

        panel.addEventListener('click', (e) => e.stopPropagation());

        dd.querySelectorAll('[data-cr-check]').forEach(cb => {
          cb.addEventListener('change', () => onCheckChange(key, dd));
        });

        dd.querySelector('[data-cr-dropdown-reset]').addEventListener('click', (e) => {
          e.stopPropagation();
          dd.querySelectorAll('[data-cr-check]').forEach(cb => { cb.checked = false; });
          onCheckChange(key, dd);
        });
      });

      document.addEventListener('click', closeAllDropdowns);
    }

    function closeAllDropdowns() {
      els.filters.querySelectorAll('.cr-dropdown').forEach(dd => {
        const panel = dd.querySelector('.cr-dropdown-panel');
        const toggle = dd.querySelector('[data-cr-dropdown-toggle]');
        if (panel) panel.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        dd.classList.remove('is-open');
      });
    }

    function onCheckChange(key, dd) {
      const checked = Array.from(dd.querySelectorAll('[data-cr-check]:checked')).map(cb => cb.value);
      const set = key === 'concern' ? state.selectedConcerns : state.selectedSkinTypes;
      set.clear();
      checked.forEach(v => set.add(v));

      const countEl = dd.querySelector('[data-cr-dropdown-count]');
      if (countEl) countEl.textContent = `${checked.length} selected`;
      dd.querySelector('[data-cr-dropdown-toggle]').classList.toggle('is-active', checked.length > 0);

      applyFilters();
    }

    function applyTypeFilter(filterId) {
      // Without an "All" chip, re-clicking the active chip deselects back to all
      if (!config.filters.all && filterId === state.activeFilter) filterId = 'all';
      state.activeFilter = filterId;
      els.filters.querySelectorAll('.cr-filter[data-filter]').forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.filter === filterId);
      });
      applyFilters();
    }

    // Recompute the visible set from every active filter dimension.
    // Quick chip = base filter; concern / skin-type dropdowns refine on top.
    // OR within a dimension's selected values, AND across dimensions.
    function applyFilters() {
      let result = state.allReviews;

      const f = state.activeFilter;
      if (f === 'photos') {
        result = result.filter(r =>
          r.has_pictures || (r.pictures && r.pictures.length) || r.before_image || r.after_image
        );
      } else if (f === '5star') {
        result = result.filter(r => r.rating === 5);
      } else if (f === 'curated') {
        result = result.filter(r => r.is_curated);
      } else if (f && f.startsWith('product:')) {
        const handle = f.replace('product:', '');
        result = result.filter(r => r.product_handle === handle);
      }

      if (state.selectedConcerns.size) {
        result = result.filter(r => {
          const arr = Array.isArray(r.concern) ? r.concern : (r.concern ? [r.concern] : []);
          return arr.some(c => state.selectedConcerns.has(c));
        });
      }

      if (state.selectedSkinTypes.size) {
        result = result.filter(r => {
          const arr = Array.isArray(r.skin_type) ? r.skin_type : (r.skin_type ? [r.skin_type] : []);
          return arr.some(s => state.selectedSkinTypes.has(s));
        });
      }

      state.filteredReviews = result;
      renderCarousel();
      renderStats();
    }

    // ============================================
    // Carousel
    // ============================================
    function renderCarousel() {
      if (!els.carousel) return;
      if (!state.filteredReviews.length) {
        els.carousel.innerHTML = '<div class="cr-empty">No reviews to show yet.</div>';
        if (els.dots) els.dots.innerHTML = '';
        return;
      }

      console.log(`[CustomReviews] Rendering ${state.filteredReviews.length} reviews on UI`);
      els.carousel.innerHTML = state.filteredReviews.map(r => renderCard(r)).join('');
      els.carousel.querySelectorAll('.cr-card').forEach(card => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('.cr-card-product')) return;
          const index = state.filteredReviews.findIndex(r => String(r.id) === card.dataset.reviewId);
          if (index !== -1) openModal(index);
        });

        // Show "Read more" only when the clamped summary/body is actually cut off.
        const readMore = card.querySelector('.cr-read-more');
        if (readMore) {
          const clamped = Array.from(card.querySelectorAll('.cr-card-text, .cr-card-summary'))
            .some(el => el.scrollHeight - el.clientHeight > 2);
          if (clamped) readMore.hidden = false;
        }
      });

      // Reset scroll to start when re-rendering (e.g., after filter change)
      els.carousel.scrollLeft = 0;
      renderDots();
    }

    function renderCard(r) {
      const hasBeforeAfter = r.before_image && r.after_image;
      const hasJudgemePics = r.pictures && r.pictures.length > 0;

      const firstConcern = Array.isArray(r.concern) ? r.concern[0] : r.concern;
      const firstSkinType = Array.isArray(r.skin_type) ? r.skin_type[0] : r.skin_type;
      const showTimeline = r.timeline && !hasBeforeAfter;
      const tagsBlock = (r.verified || firstConcern || firstSkinType || showTimeline) ? `
        <div class="cr-card-tags">
          ${r.verified ? `<span class="cr-tag">✓ Verified</span>` : ''}
          ${firstConcern ? `<span class="cr-tag">${escapeHtml(firstConcern)}</span>` : ''}
          ${firstSkinType ? `<span class="cr-tag">${escapeHtml(firstSkinType)}</span>` : ''}
          ${showTimeline ? `<span class="cr-tag">⏱ ${escapeHtml(r.timeline)}</span>` : ''}
        </div>
      ` : '';

      let imageBlock = '';
      if (hasBeforeAfter) {
        imageBlock = `
          <div class="cr-card-images">
            <div class="cr-card-image">
              <img ${imgSrc(r.before_image)} alt="Before" loading="lazy">
              <span class="cr-card-image-label">Before</span>
            </div>
            <div class="cr-card-image">
              <img ${imgSrc(r.after_image)} alt="After" loading="lazy">
              <span class="cr-card-image-label">After${r.timeline ? ' · ' + escapeHtml(r.timeline) : ''}</span>
            </div>
            ${tagsBlock}
          </div>
        `;
      } else if (hasJudgemePics) {
        imageBlock = `
          <div class="cr-card-single-image">
            <img ${imgSrc(r.pictures[0])} alt="Review photo" loading="lazy">
            ${tagsBlock}
          </div>
        `;
      } else {
        imageBlock = `<div class="cr-card-placeholder">★</div>`;
      }

      const summaryBlock = r.review_summary ? `
        <div class="cr-card-summary">
          <span class="cr-card-summary-icon" aria-hidden="true">${CR_ICONS.sparkle}</span>
          <div class="cr-card-summary-content">
            <div class="cr-card-summary-label">Result summary</div>
            <p class="cr-card-summary-text">${escapeHtml(r.review_summary)}</p>
          </div>
        </div>
      ` : '';

      const productImage = r.product_image
        ? `<img class="cr-card-product-image" ${imgSrc(r.product_image)} alt="${escapeHtml(r.product_title)}">`
        : `<div class="cr-card-product-image" style="background: var(--cr-primary-light);"></div>`;

      const productLink = r.product_handle ? `/products/${r.product_handle}` : '#';
      const productBlock = r.product_title ? `
        <a class="cr-card-product" href="${productLink}">
          ${productImage}
          <div class="cr-card-product-info">
            <div class="cr-card-product-label">Product used</div>
            <div class="cr-card-product-name">${escapeHtml(r.product_title)}</div>
          </div>
          <span class="cr-card-product-arrow">›</span>
        </a>
      ` : '';

      return `
        <article class="cr-card" data-review-id="${r.id}">
          ${imageBlock}
          <div class="cr-card-body">
            <div class="cr-card-header">
              <span class="cr-card-reviewer">${escapeHtml(r.reviewer_name)}</span>
              <span class="cr-stars">${renderStars(r.rating)}</span>
            </div>
            ${summaryBlock}
            <p class="cr-card-text">${escapeHtml(r.result_summary || r.body)}</p>
            <button type="button" class="cr-read-more" hidden>Read more</button>
            ${(hasBeforeAfter || hasJudgemePics) ? '' : tagsBlock}
            ${productBlock}
          </div>
        </article>
      `;
    }

    const CR_ICONS = {
      sparkle: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#1e6b5e" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/><path d="M19 15l.7 1.8 1.8.7-1.8.7L19 20l-.7-1.8-1.8-.7 1.8-.7L19 15z"/></svg>',
      concern: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#1e6b5e" stroke-width="1.6" stroke-linecap="round"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.5"/><path d="M12 4v2M12 18v2M4 12h2M18 12h2"/></svg>',
      skin: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#1e6b5e" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3.5s6 6.2 6 10.5a6 6 0 0 1-12 0C6 9.7 12 3.5 12 3.5z"/></svg>',
      timeline: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#1e6b5e" stroke-width="1.6" stroke-linecap="round"><rect x="4" y="5" width="16" height="16" rx="2"/><path d="M8 3v4M16 3v4M4 10h16"/></svg>',
      verified: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#1e6b5e" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>',
    };

    // One review's modal sheet; images below the opening slide load lazily.
    function renderModalSlide(r, eager) {
      const loading = eager ? '' : ' loading="lazy"';
      const hasBeforeAfter = r.before_image && r.after_image;
      const hasJudgemePics = r.pictures && r.pictures.length > 0;

      let imagesBlock = '';
      if (hasBeforeAfter) {
        imagesBlock = `
          <div class="cr-modal-images">
            <div class="cr-modal-image-wrap">
              <img src="${r.before_image}" alt="Before"${loading}>
              <span class="cr-modal-image-label">Before</span>
            </div>
            <div class="cr-modal-image-wrap">
              <img src="${r.after_image}" alt="After"${loading}>
              <span class="cr-modal-image-label">After${r.timeline ? ' · ' + escapeHtml(r.timeline) : ''}</span>
            </div>
          </div>
        `;
      } else if (hasJudgemePics) {
        const multi = r.pictures.length > 1;
        imagesBlock = `
          <div class="cr-modal-images cr-modal-images--gallery${multi ? ' cr-modal-images--scroll' : ''}">
            ${r.pictures.map(p => `
              <div class="cr-modal-image-wrap">
                <img src="${p}" alt="Review photo"${loading}>
              </div>
            `).join('')}
          </div>
        `;
      }

      const summaryBlock = r.review_summary ? `
        <div class="cr-modal-summary">
          <span class="cr-modal-summary-icon" aria-hidden="true">${CR_ICONS.sparkle}</span>
          <div class="cr-modal-summary-content">
            <div class="cr-modal-summary-label">Result summary</div>
            <p class="cr-modal-summary-text">${escapeHtml(r.review_summary)}</p>
          </div>
        </div>
      ` : '';

      // "Product used" gets its own row with the product thumbnail + link,
      // matching the review card's product block.
      const productRow = r.product_title ? `
        <a class="cr-modal-meta-row cr-modal-product-row" href="${r.product_handle ? `/products/${r.product_handle}` : '#'}">
          <span class="cr-modal-meta-label">Product used</span>
          <span class="cr-modal-meta-value cr-modal-product-value">
            ${r.product_image ? `<img class="cr-modal-product-image" src="${r.product_image}" alt="${escapeHtml(r.product_title)}" loading="lazy">` : ''}
            <span class="cr-modal-product-name">${escapeHtml(r.product_title)}</span>
          </span>
          <span class="cr-modal-product-arrow">›</span>
        </a>
      ` : '';

      const metaRows = [];
      if (r.concern) metaRows.push(['Concern', Array.isArray(r.concern) ? r.concern.join(', ') : r.concern, CR_ICONS.concern]);
      if (r.skin_type) metaRows.push(['Skin type', Array.isArray(r.skin_type) ? r.skin_type.join(', ') : r.skin_type, CR_ICONS.skin]);
      if (r.timeline) metaRows.push(['Timeline', r.timeline, CR_ICONS.timeline]);
      if (r.verified) metaRows.push(['Verified', 'Verified buyer', CR_ICONS.verified]);

      const metaBlock = (productRow || metaRows.length) ? `
        <div class="cr-modal-meta">
          ${productRow}
          ${metaRows.map(([label, value, icon]) => `
            <div class="cr-modal-meta-row">
              <span class="cr-modal-meta-label">
                ${icon ? `<span class="cr-modal-meta-icon" aria-hidden="true">${icon}</span>` : ''}
                ${escapeHtml(label)}
              </span>
              <span class="cr-modal-meta-value">${escapeHtml(value)}</span>
            </div>
          `).join('')}
        </div>
      ` : '';

      // Add to cart via the theme's shared cart pipeline: a form + .custom-add-cart
      // button is handled by the delegated listener in theme.js (posts to
      // /cart/add.js, re-renders cart sections, dispatches eurus:cart:items-changed,
      // fires Meta tracking, opens the cart drawer). Falls back to a product link.
      // Sold-out treatment (matches PDP / product cards):
      //   sold out + link(s) -> marketplace link button(s)
      //   sold out + no link -> disabled "Sold out" button
      //   in stock          -> normal add-to-cart (or product link fallback)
      // Resolve sold-out info from the product map so it applies to EVERY review
      // (manual reviews carry it inline; fetched Judge.me reviews rely on the map).
      const pInfo =
        state.productMapById[r.product_id] || state.productMap[r.product_handle] || {};
      const soldOut = r.is_sold_out === true || pInfo.sold_out === true;
      const c1 = r.channel_1 || pInfo.channel_1 || '';
      const c2 = r.channel_2 || pInfo.channel_2 || '';
      const variantId = r.variant_id || pInfo.variant_id;
      let ctaBlock = '';
      if (soldOut && (c1 || c2)) {
        const links = [
          c1 ? `<a href="${c1}" target="_blank" rel="noopener nofollow" class="cr-modal-cta cr-modal-marketplace">Buy on Amazon</a>` : '',
          c2 ? `<a href="${c2}" target="_blank" rel="noopener nofollow" class="cr-modal-cta cr-modal-marketplace">Buy on Nykaa</a>` : '',
        ].join('');
        ctaBlock = `<div class="cr-modal-cta-wrap cr-modal-cta-soldout">${links}</div>`;
      } else if (soldOut) {
        ctaBlock = `
          <div class="cr-modal-cta-wrap">
            <button type="button" class="cr-modal-cta cr-modal-cta-disabled" disabled>Sold out</button>
          </div>
        `;
      } else if (variantId) {
        ctaBlock = `
          <form class="cr-modal-cta-form cr-modal-cta-wrap" action="/cart/add" method="post">
            <input type="hidden" name="id" value="${variantId}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="custom-add-cart cr-modal-cta">Add to cart</button>
          </form>
        `;
      } else if (r.product_handle) {
        ctaBlock = `
          <div class="cr-modal-cta-wrap">
            <a href="/products/${r.product_handle}" class="cr-modal-cta">Shop this product</a>
          </div>
        `;
      }

      return `
        <div class="cr-modal-slide">
          <div class="cr-modal-content">
            <div class="cr-modal-handle" aria-hidden="true"></div>
            <div class="cr-modal-body">
              ${imagesBlock}
              <div class="cr-modal-text-content">
                <h3 class="cr-modal-reviewer">${escapeHtml(r.reviewer_name)}</h3>
                <div class="cr-modal-stars">${renderStars(r.rating)}</div>
                ${summaryBlock}
                ${metaBlock}
                <p class="cr-modal-text">${escapeHtml(r.result_summary || r.body)}</p>
                ${ctaBlock}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    // Modal is a swipeable carousel of the currently filtered reviews, opened at startIndex.
    function openModal(startIndex) {
      const existingModal = document.querySelector('.cr-modal');
      if (existingModal) existingModal.remove();

      const reviews = state.filteredReviews;
      const modal = document.createElement('div');
      modal.className = 'cr-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.innerHTML = `
        <div class="cr-modal-track">
          ${reviews.map((r, i) => renderModalSlide(r, Math.abs(i - startIndex) <= 1)).join('')}
        </div>
        <button type="button" class="cr-modal-arrow cr-modal-arrow--prev" data-cr-modal-prev aria-label="Previous review"><svg viewBox="0 0 12 12" fill="#4d4d4d" aria-hidden="true" focusable="false"><path d="M2.5 4.5 6 8l3.5-3.5" stroke="#4d4d4d" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>
        <button type="button" class="cr-modal-arrow cr-modal-arrow--next" data-cr-modal-next aria-label="Next review"><svg viewBox="0 0 12 12" fill="#4d4d4d" aria-hidden="true" focusable="false"><path d="M2.5 4.5 6 8l3.5-3.5" stroke="#4d4d4d" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>
      `;

      document.body.appendChild(modal);
      document.documentElement.classList.add('cr-modal-open');

      const track = modal.querySelector('.cr-modal-track');
      const prevBtn = modal.querySelector('[data-cr-modal-prev]');
      const nextBtn = modal.querySelector('[data-cr-modal-next]');
      let currentIndex = startIndex;

      const updateArrows = () => {
        prevBtn.disabled = currentIndex <= 0;
        nextBtn.disabled = currentIndex >= reviews.length - 1;
      };
      const goTo = (index, smooth = true) => {
        const target = Math.max(0, Math.min(index, reviews.length - 1));
        track.scrollTo({ left: target * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      };

      goTo(startIndex, false);
      updateArrows();
      prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
      nextBtn.addEventListener('click', () => goTo(currentIndex + 1));

      // Mouse drag to swipe (touch already swipes natively via scroll-snap).
      let drag = null;
      let suppressClick = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        drag = { x: e.clientX, scroll: track.scrollLeft, id: e.pointerId, moved: false };
      });
      track.addEventListener('pointermove', (e) => {
        if (!drag) return;
        const deltaX = e.clientX - drag.x;
        if (!drag.moved) {
          if (Math.abs(deltaX) < 6) return;
          // Capture only once dragging, so plain clicks keep their real target.
          drag.moved = true;
          track.setPointerCapture(drag.id);
          modal.classList.add('is-dragging');
        }
        track.scrollLeft = drag.scroll - deltaX;
      });
      const endDrag = (e) => {
        if (!drag) return;
        const { moved, x } = drag;
        drag = null;
        if (!moved) return;
        suppressClick = true;
        setTimeout(() => { suppressClick = false; }, 0);
        const deltaX = e.clientX - x;
        const step = Math.abs(deltaX) > 60 ? (deltaX < 0 ? 1 : -1) : 0;
        goTo(currentIndex + step);
        // Re-enable snapping after the smooth scroll settles, or it jumps mid-animation.
        setTimeout(() => modal.classList.remove('is-dragging'), 400);
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      // A drag must not count as a click (backdrop close, links, add to cart).
      modal.addEventListener('click', (e) => {
        if (!suppressClick) return;
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);

      let scrollFrame;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          if (drag && drag.moved) return;
          currentIndex = Math.round(track.scrollLeft / track.clientWidth);
          updateArrows();
        });
      }, { passive: true });

      // Force a reflow so the entrance transition (opacity + translateY) runs.
      modal.getBoundingClientRect();
      modal.classList.add('is-open');

      const closeModal = () => {
        if (modal.classList.contains('is-closing')) return;
        modal.classList.add('is-closing');
        modal.classList.remove('is-open');
        document.documentElement.classList.remove('cr-modal-open');
        document.removeEventListener('keydown', onKeydown);
        setTimeout(() => modal.remove(), MODAL_ANIM_MS);
      };

      const onKeydown = (e) => {
        if (e.key === 'Escape') closeModal();
        else if (e.key === 'ArrowLeft') goTo(currentIndex - 1);
        else if (e.key === 'ArrowRight') goTo(currentIndex + 1);
      };
      document.addEventListener('keydown', onKeydown);

      // Backdrop click (the gutters around a sheet are the track/slide, not content).
      modal.addEventListener('click', (e) => {
        if (e.target === modal || e.target === track || e.target.classList.contains('cr-modal-slide')) {
          closeModal();
        }
      });

      // Swipe down to close (mobile). Only counts when the touched sheet is
      // scrolled to the top at gesture start, so it never fights with scrolling.
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartTime = 0;
      let touchAtTop = false;

      track.addEventListener('touchstart', (e) => {
        const content = e.target.closest('.cr-modal-content');
        const touch = e.touches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchStartTime = Date.now();
        touchAtTop = !!content && content.scrollTop <= 0;
      }, { passive: true });

      track.addEventListener('touchend', (e) => {
        if (!touchAtTop) return;
        const touch = e.changedTouches[0];
        const deltaY = touch.clientY - touchStartY;
        const deltaX = Math.abs(touch.clientX - touchStartX);
        const elapsed = Date.now() - touchStartTime;
        // Downward, mostly vertical, and either a quick flick or a long pull.
        if (deltaY > 70 && deltaY > deltaX * 1.5 && (elapsed < 500 || deltaY > 140)) {
          closeModal();
        }
      }, { passive: true });
    }

    // ============================================
    // Carousel navigation
    // ============================================
    function setupCarouselNav() {
      // Bind every prev/next control (desktop side arrows + mobile inline arrows).
      section.querySelectorAll('[data-cr-prev]').forEach(b => b.addEventListener('click', () => scrollCarousel(-1)));
      section.querySelectorAll('[data-cr-next]').forEach(b => b.addEventListener('click', () => scrollCarousel(1)));
      els.carousel.addEventListener('scroll', updateActiveDot);
    }

    function setupResizeHandler() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          renderDots();
        }, 200);
      });
    }

    function scrollCarousel(direction) {
      const card = els.carousel.querySelector('.cr-card');
      if (!card) return;
      const cardStep = card.offsetWidth + 16;
      els.carousel.scrollBy({ left: direction * cardStep, behavior: 'smooth' });
      setTimeout(updateActiveDot, 350);
    }

    function getVisiblePerView() {
      const card = els.carousel.querySelector('.cr-card');
      if (!card) return 1;
      const cardStep = card.offsetWidth + 16;
      return Math.max(1, Math.floor(els.carousel.offsetWidth / cardStep));
    }

    function renderDots() {
      if (!els.dots) return;
      const total = state.filteredReviews.length;
      if (!total) {
        els.dots.innerHTML = '';
        return;
      }

      const visiblePerView = getVisiblePerView();
      const pages = Math.ceil(total / visiblePerView);

      if (pages <= 1) {
        els.dots.innerHTML = '';
        return;
      }

      // Render at most MAX_DOTS dots; each dot's target page is assigned
      // dynamically (sliding window) in updateActiveDot().
      const dotCount = Math.min(pages, MAX_DOTS);
      els.dots.innerHTML = Array.from({ length: dotCount }, () =>
        `<button class="cr-dot" type="button" aria-label="Go to page"></button>`
      ).join('');

      els.dots.querySelectorAll('.cr-dot').forEach(dot => {
        dot.addEventListener('click', () => {
          const page = parseInt(dot.dataset.page, 10);
          if (!isNaN(page)) scrollToPage(page);
        });
      });

      updateActiveDot();
    }

    function scrollToPage(page) {
      const card = els.carousel.querySelector('.cr-card');
      if (!card) return;
      const visiblePerView = getVisiblePerView();
      const cardStep = card.offsetWidth + 16;
      const maxScroll = els.carousel.scrollWidth - els.carousel.clientWidth;
      const pages = Math.ceil(state.filteredReviews.length / visiblePerView);
      const isLastPage = page >= pages - 1;
      const target = isLastPage ? maxScroll : page * visiblePerView * cardStep;
      els.carousel.scrollTo({ left: target, behavior: 'smooth' });
    }

    function updateActiveDot() {
      if (!els.dots) return;
      const dots = els.dots.querySelectorAll('.cr-dot');
      if (!dots.length) return;
      const card = els.carousel.querySelector('.cr-card');
      if (!card) return;

      const visiblePerView = getVisiblePerView();
      const cardStep = card.offsetWidth + 16;
      const pageWidth = visiblePerView * cardStep;
      const scrollLeft = els.carousel.scrollLeft;
      const maxScroll = els.carousel.scrollWidth - els.carousel.clientWidth;
      const pages = Math.ceil(state.filteredReviews.length / visiblePerView);

      let currentPage;
      if (maxScroll - scrollLeft < 20) {
        currentPage = pages - 1;
      } else {
        currentPage = Math.floor((scrollLeft + pageWidth / 2) / pageWidth);
        currentPage = Math.max(0, Math.min(currentPage, pages - 1));
      }

      // Slide the dot window so the active page stays roughly centered, and flag
      // edge dots (smaller) when there are more pages beyond the visible window.
      const dotCount = dots.length;
      let windowStart = 0;
      if (pages > dotCount) {
        windowStart = Math.min(Math.max(currentPage - Math.floor(dotCount / 2), 0), pages - dotCount);
      }

      dots.forEach((d, i) => {
        const page = windowStart + i;
        d.dataset.page = page;
        d.classList.toggle('is-active', page === currentPage);
        const isEdge =
          (i === 0 && windowStart > 0) ||
          (i === dotCount - 1 && windowStart + dotCount < pages);
        d.classList.toggle('cr-dot--edge', isEdge);
      });
    }
  }

  // Stars come from the JudgemeStar icon font via CSS content
  // (\e000 = full star, \e001 = empty star)
  function renderStars(rating) {
    const filled = Math.round(rating || 0);
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      stars += `<span class="cr-star${i <= filled ? '' : ' cr-star-empty'}" aria-hidden="true"></span>`;
    }
    return stars;
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
})();